// NPM Imports
import uuid from "react-native-uuid";
import { convertToRaw } from "draft-js";

// Components Imports
import { Toastr } from "../../revvComponents";
// Internals imports
import { TEMPLATE, WORKFLOW, DOCUMENTS } from "../../constants";
import { getSectionFormFields } from "../../utils/editorUtils";
import { getTableDBContent } from "../../utils/documentUtils";

// Destructuring
const { APPROVAL_TYPE, APPROVAL_TYPE_MAP, TEMP_FORM_FIELD_DATA } = TEMPLATE;
const { OPERATORS_MAPPING } = WORKFLOW;
const { DOCUMENT_MODE } = DOCUMENTS;

const _showToastr = message => {
  Toastr.showToastr({
    type: "warning",
    message,
    timeout: 2000,
  });
};

export const _generateActionLevels = (
  usersReverseHashMap,
  userGroupReverseHashMap,
  action,
) => {
  const payload = [];
  const approvalType = action.approvalType.value || action.approvalType;

  if (approvalType === "Sequential") {
    action.approvers.forEach((approver, index) => {
      // Generate User JSON
      if (approver.type === "user") {
        payload.push({
          type: "UNANIMOUS",
          order: index + 1,
          approvers: [
            {
              id: usersReverseHashMap[approver.value || approver.name],
            },
          ],
        });
      }
      // Generate Group JSON
      else {
        payload.push({
          type: "UNANIMOUS",
          order: index + 1,
          group_approvers: [
            {
              group_id:
                userGroupReverseHashMap[approver.value || approver.name],
            },
          ],
        });
      }
    });
  } else {
    const users = action.approvers.filter(approver => approver.type === "user");
    const groups = action.approvers.filter(
      approver => approver.type === "group",
    );

    payload.push({
      type: "UNANIMOUS",
      order: 1,
      approvers: users.map(user => {
        const obj = { id: usersReverseHashMap[user.value || user.name] };
        return obj;
      }),
      group_approvers: groups.map(user => {
        const obj = {
          group_id: userGroupReverseHashMap[user.value || user.name],
        };
        return obj;
      }),
    });
  }
  return payload;
};

export const formatClauseOption = clause => {
  const {
    name,
    type,
    description,
    clauses,
    defaultValue,
    labelEnabled,
    catalogFinalValue,
    catalogFinalOption,
  } = clause;
  let options = [];
  let metaValues;
  let compType;
  if (clause.isImageFormField) compType = "IMAGE";
  if (clause.isTableFormField) compType = "TABLE";
  if (clause.isTableFormField && clause.tableFieldType) compType = "TABLE_API";
  if (clause.isImageFormField) {
    options = clauses.map(cl => ({
      id: cl.id,
      value: cl.data,
    }));
    metaValues = {
      default: defaultValue,
      subtype: type,
      clauses: options,
    };
  } else if (clause.isTableFormField && clause.tableFieldType) {
    metaValues = defaultValue;
  } else if (clause.isTableFormField) {
    metaValues = {
      default: getTableDBContent(defaultValue, DOCUMENT_MODE.TEMPLATE),
    };
  } else {
    options = clauses.map(cl => ({
      id: cl.id,
      label: cl.label,
      value: JSON.stringify(convertToRaw(cl.data)),
    }));
    metaValues = {
      default: defaultValue,
      subtype: type,
      clauses: options,
      label_enabled: labelEnabled,
      catalog_display: catalogFinalOption,
      catalog_column:
        (catalogFinalValue && catalogFinalValue.name) ||
        (catalogFinalValue && catalogFinalValue.value),
    };
  }
  const clauseOption = {
    description,
    label: name,
    field_name: name,
    type:
      clause.isImageFormField || clause.isTableFormField ? compType : "CLAUSE",
    source: "form",
    meta: metaValues,
  };

  return clauseOption;
};

export const generateTempFormFiled = (response, metaData, scopeType) => {
  const payload = [];
  const rules = (response && response.rules) || [response].flat();
  const workFlowType = (response && response.scope_type) || scopeType;
  rules.forEach(data => {
    // get "action" and "condition" form "workflow.js" constant file
    let tempWFAction = {
      ...TEMP_FORM_FIELD_DATA.tempWFAction,
      approvers: [{}],
    };
    let tempWFCondition = {
      ...TEMP_FORM_FIELD_DATA.tempWFCondition,
      conditions: [],
    };

    let id = uuid.v4();

    if (data && data.conditions) {
      // set the rule id
      id = data && data.id;
      const { usersHasMap, userGroupHasMap } = metaData;

      if (workFlowType === "TEMPLATE_DYNAMICFIELD") {
        data.conditions.forEach(condition => {
          condition.rel_op = OPERATORS_MAPPING[condition.rel_op];
          delete condition.sub_param;
        });
      }

      // Code to update condition JSON
      const conditionPayload = Object.assign(tempWFCondition, {
        conditions: data.conditions,
        logicalOperator: data.logical_op,
      });
      tempWFCondition = conditionPayload;

      // Code to update action JSON
      const actions = data.action.data;
      const approvalType = actions.type;

      const approversList = [];
      actions.levels.forEach(item => {
        const users =
          (item.approvers &&
            item.approvers.map(approver => {
              approver.value = usersHasMap[approver.id];
              approver.label = usersHasMap[approver.id];
              approver.type = "user";
              delete approver.user_email; // Added to handle add approver validation
              return approver;
            })) ||
          [];
        const userGroup =
          (item.group_approvers &&
            item.group_approvers.map(approver => {
              approver.value = userGroupHasMap[approver.group_id];
              approver.label = userGroupHasMap[approver.group_id];
              approver.type = "group";
              delete approver.group_name; // Added to handle add approver validation
              delete approver.user_email; // Added to handle add approver validation
              return approver;
            })) ||
          [];
        const combinedData = [...users, ...userGroup];
        approversList.push(combinedData);
      });
      const actionPayload = Object.assign(tempWFAction, {
        approvalType: APPROVAL_TYPE_MAP[approvalType],
        approvers: approversList.flat(),
        id: data.action.id,
        triggerMessage: data.action.trigger_message,
      });
      tempWFAction = actionPayload;
    }
    payload.push({ id, tempWFAction, tempWFCondition });
  });

  return payload;
};

export const validateTempWorkFlowFields = (rules, dynamicField) =>
  rules.every((rule, index) => {
    const { tempWFCondition: condition, tempWFAction: action } = rule;
    const ruleIndex = index + 1;
    /*
    ==========================================
          Validate Condition View Data
    ==========================================
    */
    // Check if rule is selected

    if (condition.conditions.length === 0) {
      _showToastr(`Kindly add atleast one condition in Rule ${ruleIndex}`);
      return false;
    }

    // CHECK IF ALL THE CONDITION FILEDS ARE FILLED
    let isAllConditionsFilled = true;
    if (
      !(
        dynamicField &&
        dynamicField.meta &&
        dynamicField.meta.subtype === "free_text"
      )
    ) {
      condition.conditions.forEach(rows => {
        const isAllEmpty = Object.values(rows).every(value => value !== "");
        if (!isAllEmpty) {
          isAllConditionsFilled = false;
          return false;
        }
        return true;
      });
    }

    if (!isAllConditionsFilled) {
      _showToastr(`Kindly fill all condition fields in Rule ${ruleIndex}`);
      return false;
    }

    /*
    ==========================================
          Validate Action View Data
    ==========================================
    */

    if (!Object.keys(action.approvalType).length > 0) {
      _showToastr(`Kindly select an approval type in Rule ${ruleIndex}`);
      return false;
    }

    if (
      action.approvers.length === 0 ||
      !Object.values(action.approvers[0]).length > 0
    ) {
      _showToastr(`Kindly select atleast one approver in Rule ${ruleIndex}`);
      return false;
    }

    // CHECK IF ALL THE APPROVERS FILEDS ARE FILLED
    let isAllApproversFilled = true;
    action.approvers.forEach(rows => {
      const isAllEmpty = Object.values(rows).every(value => value !== "");
      if (!isAllEmpty) {
        isAllApproversFilled = false;
        return false;
      }
      return true;
    });
    if (!isAllApproversFilled) {
      _showToastr(`Kindly fill all approver fields in Rule ${ruleIndex}`);
      return false;
    }

    if (action.triggerMessage === "") {
      _showToastr(`Trigger Message can't be empty in Rule ${ruleIndex}!`);
      return false;
    }
    return true;
  });

export const generatePayloadForTempWorkFlow = (formFields, metaData) => {
  const payload = [];
  const { usersReverseHashMap, userGroupReverseHashMap } = metaData;
  formFields.forEach((info, index) => {
    const { tempWFCondition: condition, tempWFAction: action, id } = info;

    const obj = {
      id,
      action: {
        type: "APPROVAL",
        data: {
          id: action.id,
          type: APPROVAL_TYPE[action.approvalType],
          levels: _generateActionLevels(
            usersReverseHashMap,
            userGroupReverseHashMap,
            action,
          ),
        },
        trigger_message: action.triggerMessage,
      },
      conditions: condition.conditions,
      isDirty: info.isDirty || false,
      logical_op: condition.logicalOperator,
      order: index + 1,
    };

    if (typeof info.id === "string") delete obj.id;
    payload.push(obj);
  });

  return payload;
};

export const generateTempWrkFlowMetaData = (user, userGroup) => {
  const props = {};
  const usersReverseHashMap = {};
  const usersHasMap = {};
  const userGroupReverseHashMap = {};
  const userGroupHasMap = {};

  // Generate Reverse Hash Map for Users
  user.users.forEach(item => {
    usersReverseHashMap[item.name] = item.id;
  });

  // Generate Forward Hash Map for Users
  user.users.forEach(item => {
    usersHasMap[item.id] = item.name;
  });

  // Generate Reverse Hash Map for Users
  userGroup.Usergroups.forEach(item => {
    userGroupReverseHashMap[item.name] = item.id;
  });

  // Generate Forward Hash Map for Users
  userGroup.Usergroups.forEach(item => {
    userGroupHasMap[item.id] = item.name;
  });

  // Append mutations to props
  props.usersHasMap = usersHasMap;
  props.usersReverseHashMap = usersReverseHashMap;
  props.userGroupHasMap = userGroupHasMap;
  props.userGroupReverseHashMap = userGroupReverseHashMap;
  return props;
};

export const parseTempWrkFlowData = (response, metaData) => {
  const rules = response.rules || [response].flat();
  /**
   * 3 possible scenarios can occur
   * 1) No workflow attached  // Convert to array
   * 2) Get workflow details(response.rules) // typeof array
   * 3) On save/update workflow callback (response) // typeof array
   */
  rules.forEach(data => {
    if (data && data.conditions) {
      // Replace the mail id's to name
      const actions = data.action.data;

      // Approvers reverse map
      actions.levels.forEach(level => {
        if (level.approvers) {
          level.approvers.forEach(item => {
            item.name = metaData.usersHasMap[item.id];
          });
        }
        if (level.group_approvers) {
          level.group_approvers.forEach(item => {
            item.name = metaData.userGroupHasMap[item.group_id];
          });
        }
      });
    }
  });

  return response;
};

export const sortTemplatesList = templates => {
  const sortedList = templates.sort(
    (a, b) => new Date(b.created_at) - new Date(a.created_at),
  );
  return sortedList;
};

export const splitTemplateSearchContent = searchContent => {
  const templateSearchContent = {
    page: searchContent.page_num,
    count: searchContent.count,
    hasMore: searchContent.has_more,
  };

  return {
    templateSearchContent,
    templates: searchContent.templates,
  };
};

export const getFormFieldSectionMapping = components => {
  const fieldSectionMapping = {};
  Object.keys(components).forEach(componentId => {
    const section = components[componentId].content;
    const sectionID = section.section_id || section.id;
    // eslint-disable-next-line no-unused-vars
    const fieldList = getSectionFormFields(section);
    const fieldIDList = new Set([
      ...fieldList.map(field => field.dynamicFieldId),
    ]);
    fieldIDList.forEach(fieldID => {
      if (fieldID in fieldSectionMapping) {
        const oldSectionList = fieldSectionMapping[fieldID];
        oldSectionList.push(sectionID);
        fieldSectionMapping[fieldID] = oldSectionList;
      } else {
        fieldSectionMapping[fieldID] = [sectionID];
      }
    });
  });
  return fieldSectionMapping;
};

export const filterOutTemplatesByStatus = (templates, filters) =>
  templates.filter(i => filters.includes(i.status.toUpperCase()));
